import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import {
  Menu, X, LogOut, Settings,
  LayoutDashboard, Bookmark, CreditCard, ImageIcon, Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AuthModal from './AuthModal';
import NotificationBell from './NotificationBell';
import IvisoraLogo from '../shared/IvisoraLogo';
import { isTokenExpired, scheduleAutoLogout, AUTH_CHANGED_EVENT } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';
const ACTIVITY_KEY = 'ivisora_activity';
const AVATAR_PHOTO_KEY = 'ivisora_avatar_photo';
const AVATAR_GRADIENT_KEY = 'ivisora_avatar_gradient';

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #7c3aed, #ec4899)',
  'linear-gradient(135deg, #2563eb, #38bdf8)',
  'linear-gradient(135deg, #059669, #34d399)',
  'linear-gradient(135deg, #d97706, #fbbf24)',
  'linear-gradient(135deg, #dc2626, #f87171)',
  'linear-gradient(135deg, #0891b2, #67e8f9)',
];

const pushActivity = (type, title) => {
  try {
    const acts = JSON.parse(localStorage.getItem(ACTIVITY_KEY) || '[]');
    acts.unshift({ type, title, timestamp: new Date().toISOString() });
    localStorage.setItem(ACTIVITY_KEY, JSON.stringify(acts.slice(0, 50)));
  } catch {}
};

/* ─── Dropdown item ─────────────────────────────────────────────────────────── */
const MenuItem = ({ icon: Icon, label, onClick, badge, danger }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 group"
    style={{
      color: danger ? '#dc2626' : '#374151',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      textAlign: 'left',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.background = danger ? '#fef2f2' : '#f5f3ff';
      e.currentTarget.style.color = danger ? '#b91c1c' : '#7c3aed';
      e.currentTarget.querySelector('.menu-icon')?.style && (e.currentTarget.querySelector('.menu-icon').style.color = danger ? '#b91c1c' : '#7c3aed');
    }}
    onMouseLeave={e => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = danger ? '#dc2626' : '#374151';
    }}
  >
    <Icon className="menu-icon w-4 h-4 flex-shrink-0 transition-colors" style={{ color: danger ? '#dc2626' : '#9ca3af' }} />
    <span className="flex-1 font-medium">{label}</span>
    {badge && (
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-600">
        {badge}
      </span>
    )}
  </button>
);

/* ─── Logout confirmation modal ────────────────────────────────────────────── */
const LogoutConfirm = ({ onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-[200] flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)', animation: 'lg-overlay 0.2s ease-out' }}
    onClick={onCancel}
  >
    <style>{`
      @keyframes lg-overlay { from{opacity:0} to{opacity:1} }
      @keyframes lg-card { from{opacity:0;transform:scale(0.93) translateY(10px)} to{opacity:1;transform:none} }
    `}</style>
    <div
      className="relative w-full max-w-sm bg-white rounded-2xl p-6 shadow-2xl"
      style={{ animation: 'lg-card 0.25s cubic-bezier(0.22,1,0.36,1)' }}
      onClick={e => e.stopPropagation()}
    >
      {/* Icon */}
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <LogOut className="w-5 h-5 text-red-600" />
      </div>
      <h3 className="text-center text-lg font-bold text-gray-900 mb-1">Log out of iVisora?</h3>
      <p className="text-center text-sm text-gray-500 mb-6">You'll need to sign in again to access your designs.</p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="flex-1 h-11 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
        >
          Log out
        </button>
      </div>
    </div>
  </div>
);

/* ─── Header component ──────────────────────────────────────────────────────── */
const Header = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [user, setUser] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [avatarPhoto, setAvatarPhoto] = useState(null);
  const [avatarGradient, setAvatarGradient] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const autoLogoutRef = useRef(null);

  // Toggle blurred-glass styling once the user has scrolled past 10px.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Anchor-aware nav: scroll to a section if already on /, otherwise navigate to /#anchor
  const goToAnchor = (anchorId) => {
    if (window.location.pathname === '/') {
      const el = document.getElementById(anchorId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else navigate(`/#${anchorId}`);
    } else {
      navigate(`/#${anchorId}`);
    }
  };

  // Cancel any pending auto-logout timer when the component unmounts
  useEffect(() => () => { if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current); }, []);

  // Listen for localStorage changes (e.g., avatar updated in UserSettings)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === AVATAR_PHOTO_KEY) setAvatarPhoto(e.newValue);
      if (e.key === AVATAR_GRADIENT_KEY && e.newValue !== null) setAvatarGradient(parseInt(e.newValue, 10));
      if (e.key === USER_KEY && e.newValue) {
        try { setUser(JSON.parse(e.newValue)); } catch {}
      }
    };
    // Same-tab sign-in/out made through another copy of AuthModal on the page
    // (the 'storage' event only fires for other tabs).
    const onAuthChanged = () => {
      try { setUser(JSON.parse(localStorage.getItem(USER_KEY) || 'null')); } catch { setUser(null); }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    };
  }, []);

  // Restore session
  useEffect(() => {
    // Load avatar appearance from localStorage
    const savedPhoto = localStorage.getItem(AVATAR_PHOTO_KEY);
    if (savedPhoto) setAvatarPhoto(savedPhoto);
    const savedGrad = localStorage.getItem(AVATAR_GRADIENT_KEY);
    if (savedGrad !== null) setAvatarGradient(parseInt(savedGrad, 10));

    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    // ── Fast local expiry check (no network round-trip) ───────────────────────
    if (token && isTokenExpired(token)) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
      return;
    }

    if (!token || !API_URL) {
      const savedUser = localStorage.getItem(USER_KEY);
      if (savedUser && !token) localStorage.removeItem(USER_KEY);
      // Still restore from localStorage if token exists but no API
      const u = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
      if (u) setUser(u);
      return;
    }

    const validateSession = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const u = { id: data.id, name: data.name, email: data.email, plan: data.plan || 'free', profile_photo: data.profile_photo || null };
          setUser(u);
          localStorage.setItem(USER_KEY, JSON.stringify(u));
          // Sync avatar photo from server
          if (data.profile_photo) {
            setAvatarPhoto(data.profile_photo);
            localStorage.setItem(AVATAR_PHOTO_KEY, data.profile_photo);
          }
          // Schedule automatic logout when this token's exp is reached
          if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
          autoLogoutRef.current = scheduleAutoLogout(
            token,
            [AUTH_TOKEN_KEY, USER_KEY],
            () => {
              setUser(null);
              toast({ title: 'Session expired', description: 'Please sign in again.' });
            },
          );
        } else {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setUser(null);
        }
      } catch {
        // Network error — still show user from localStorage
        const u = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
        if (u) setUser(u);
      }
    };
    validateSession();
  }, []);

  const handleAuthSuccess = (userData, token) => {
    setUser(userData);
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    pushActivity('login', 'Signed in to iVisora');
    // Schedule automatic logout when this token expires
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    autoLogoutRef.current = scheduleAutoLogout(
      token,
      [AUTH_TOKEN_KEY, USER_KEY],
      () => {
        setUser(null);
        toast({ title: 'Session expired', description: 'Please sign in again.' });
      },
    );
  };

  const handleLogout = () => {
    if (autoLogoutRef.current) clearTimeout(autoLogoutRef.current);
    setUser(null);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setShowLogoutConfirm(false);
    toast({ title: 'Logged out', description: 'You have been logged out successfully' });
    navigate('/');
  };

  const openAuthModal = (tab) => {
    setAuthTab(tab);
    setAuthModalOpen(true);
  };

  // Avatar
  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const avatarBg = AVATAR_GRADIENTS[avatarGradient] || AVATAR_GRADIENTS[0];

  const headerClass = scrolled
    ? 'bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-[0_2px_20px_-12px_rgba(15,23,42,0.18)]'
    : 'bg-transparent border-b border-transparent';

  return (
    <>
      <header className={`sticky top-0 z-50 transition-colors duration-300 ${headerClass}`}>
        {/* Match the homepage container width (max-w-[1320px], px-6 lg:px-8)
            so the logo aligns with the hero heading and the Sign Up button
            aligns with the mockup's right edge. */}
        <div className="container mx-auto px-6 lg:px-8 max-w-[1320px]">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label="iVisora — home"
              className="flex items-center cursor-pointer select-none bg-transparent border-0 p-0 -ml-0.5"
            >
              <IvisoraLogo glow className="h-7 sm:h-8 md:h-9" />
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-9">
              <button onClick={() => goToAnchor('features')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">Features</button>
              <button onClick={() => navigate('/templates')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">Templates</button>
              <button onClick={() => navigate('/marketplace')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">Marketplace</button>
              <button onClick={() => goToAnchor('ai-tools')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">AI Tools</button>
              <button onClick={() => navigate('/blog')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">Blog</button>
              <button onClick={() => navigate('/pricing')} className="text-[14.5px] text-slate-700 hover:text-violet-700 font-medium tracking-tight transition-colors">Pricing</button>

              {user ? (
                <>
                {/* C11 — notification bell, logged-in users only */}
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 h-10 px-1 rounded-xl transition-all duration-200 outline-none"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.querySelector('.avatar-ring') && (e.currentTarget.querySelector('.avatar-ring').style.boxShadow = '0 0 0 3px rgba(124,58,237,0.25)')}
                      onMouseLeave={e => e.currentTarget.querySelector('.avatar-ring') && (e.currentTarget.querySelector('.avatar-ring').style.boxShadow = 'none')}
                    >
                      <div
                        className="avatar-ring w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-white text-sm font-bold flex-shrink-0 transition-all duration-200"
                        style={{ background: avatarPhoto ? 'transparent' : avatarBg, boxShadow: 'none' }}
                      >
                        {avatarPhoto
                          ? <img src={avatarPhoto} alt="avatar" className="w-full h-full object-cover" />
                          : initials}
                      </div>
                      <span className="text-sm font-semibold text-gray-800 max-w-[100px] truncate">{user.name.split(' ')[0]}</span>
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    className="w-[260px] p-0 rounded-2xl border-0 overflow-hidden"
                    style={{
                      boxShadow: '0 10px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
                      animation: 'none',
                    }}
                    sideOffset={8}
                  >
                    {/* User info header */}
                    <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50/40 border-b border-violet-100/60">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-11 h-11 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                          style={{ background: avatarPhoto ? 'transparent' : avatarBg, boxShadow: '0 4px 14px rgba(124,58,237,0.25)' }}
                        >
                          {avatarPhoto
                            ? <img src={avatarPhoto} alt="avatar" className="w-full h-full object-cover" />
                            : initials}
                        </div>
                        <div className="overflow-hidden">
                          <div className="font-semibold text-sm text-gray-900 truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        </div>
                      </div>
                    </div>

                    {/* Main actions */}
                    <div className="p-1.5">
                      <MenuItem icon={LayoutDashboard} label="Dashboard" onClick={() => navigate('/dashboard')} />
                      <MenuItem icon={ImageIcon} label="My Designs" onClick={() => navigate('/my-designs')} />
                      <MenuItem icon={Users} label="Shared with me" onClick={() => navigate('/shared-with-me')} />
                      <MenuItem icon={Bookmark} label="Saved Files" onClick={() => navigate('/saved')} />
                    </div>

                    <DropdownMenuSeparator className="mx-2 my-0" />

                    {/* Account */}
                    <div className="p-1.5">
                      <MenuItem icon={Settings} label="Account Settings" onClick={() => navigate('/settings')} />
                      <MenuItem icon={CreditCard} label="Billing" badge="Soon" onClick={() => {}} />
                    </div>

                    <DropdownMenuSeparator className="mx-2 my-0" />

                    {/* Logout */}
                    <div className="p-1.5">
                      <MenuItem icon={LogOut} label="Log out" onClick={() => setShowLogoutConfirm(true)} danger />
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center gap-3 ml-3">
                  <button
                    onClick={() => openAuthModal('login')}
                    className="text-[14.5px] font-medium text-slate-700 hover:text-violet-700 tracking-tight transition-colors px-2 h-10"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => openAuthModal('signup')}
                    className="group relative inline-flex items-center justify-center h-10 px-5 rounded-full text-[14px] font-semibold text-white tracking-tight overflow-hidden transition-all duration-300 ease-out hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)',
                      boxShadow: '0 1px 0 0 rgba(255,255,255,0.18) inset, 0 10px 22px -8px rgba(124,58,237,0.55), 0 2px 6px -2px rgba(124,58,237,0.35)',
                    }}
                  >
                    <span className="relative z-10">Sign Up</span>
                    <span
                      aria-hidden
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 50%, #db2777 100%)' }}
                    />
                  </button>
                </div>
              )}
            </nav>

            {/* Mobile menu button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <nav className="flex flex-col gap-3">
                <button onClick={() => { goToAnchor('features'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Features</button>
                <button onClick={() => { navigate('/templates'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Templates</button>
                <button onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Marketplace</button>
                <button onClick={() => { goToAnchor('ai-tools'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">AI Tools</button>
                <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Blog</button>
                <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Pricing</button>
                {user ? (
                  <>
                    <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Dashboard</button>
                    <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="text-gray-700 hover:text-purple-600 font-medium text-left py-1">Settings</button>
                    <Button variant="outline" className="w-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowLogoutConfirm(true)}>Log out</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full font-medium" onClick={() => { openAuthModal('login'); setMobileMenuOpen(false); }}>Login</Button>
                    <Button variant="outline" className="w-full font-medium" onClick={() => { openAuthModal('signup'); setMobileMenuOpen(false); }}>Sign Up</Button>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        defaultTab={authTab}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
      />

      {/* Logout confirmation */}
      {showLogoutConfirm && (
        <LogoutConfirm
          onCancel={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogout}
        />
      )}
    </>
  );
};

export default Header;
